import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "hvac-seo-colorado",
  metaTitle: "HVAC SEO in Colorado | Local SEO for HVAC Companies | Colorado Web Impressions",
  metaDescription:
    "HVAC SEO across Colorado — Map Pack rankings, year-round lead flow, and content built for the seasonal HVAC buying cycle. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "HVAC marketing", to: "/hvac-marketing-colorado" },
  ],
  currentLabel: "HVAC SEO Colorado",
  eyebrow: "HVAC SEO — Colorado",
  h1: (
    <>
      HVAC <span className="text-brand-gold">SEO</span> in Colorado
    </>
  ),
  heroDescription:
    "Year-round Colorado HVAC lead flow — emergency repair searches, seasonal tune-ups, and big-ticket replacements. Built by Chris from our Colorado Springs office.",
  intro:
    "Colorado HVAC SEO is a seasonal game most agencies get wrong — they ride the summer/winter peaks and disappear in shoulder months. We map your service pages to the actual Colorado HVAC buying cycle so you capture furnace repair searches when the first cold front hits, AC install searches when summer breaks, and tune-up traffic in spring and fall. The result is steady, year-round lead flow across every Colorado market you serve.",
  definition: {
    term: "HVAC SEO in Colorado",
    body: "HVAC SEO is the process of ranking heating and cooling companies for high-intent local searches like 'AC repair Colorado Springs', 'furnace install Denver', or 'HVAC tune-up near me.' It combines local Map Pack optimization, on-page service content tied to the seasonal buying cycle, and structured review velocity.",
  },
  whatWeDo: [
    { title: "Service pages mapped to the buying cycle", body: "Furnace repair, AC repair, install, replace, tune-up, IAQ — each with its own ranking page and seasonal targeting." },
    { title: "Map Pack work for every Colorado service area", body: "Colorado Springs, Denver, Fort Collins, Pueblo, Castle Rock — dedicated pages and GBP service-area tuning for each market." },
    { title: "Review velocity tied to job completion", body: "Structured review-request workflow tied to your FSM software so reviews come in steadily — not in unnatural spikes." },
    { title: "Seasonal content calendar", body: "We publish HVAC content ahead of the cold front, the summer break, and the shoulder months — so you rank when the searches happen, not after." },
    { title: "Schema for HVAC trust signals", body: "LocalBusiness, Service, FAQPage, and Review schema — plus NATE/EPA trust signals surfaced in your on-page structure." },
    { title: "Page speed and mobile conversion", body: "HVAC searches are 70%+ mobile and 80%+ emergency-intent. We hand-code fast sites with sticky click-to-call and after-hours CTAs." },
  ],
  whyThisMarket: [
    {
      heading: "Colorado HVAC search is seasonal — but always-on",
      body: "Colorado has a brutally efficient HVAC buying cycle: every fall cold snap and every summer heat wave triggers a search spike. Companies that publish ahead of those windows compound — companies that publish after them lose the season.",
    },
    {
      heading: "National lead-gen sites dominate generic terms",
      body: "Angi, HomeAdvisor, and Thumbtack outrank most Colorado HVAC companies on city-level terms like 'HVAC Colorado Springs.' We win by going neighborhood-deep — Briargate, Falcon, Highlands Ranch, Stapleton — where the aggregators don't compete as hard.",
    },
    {
      heading: "Reviews and recency carry disproportionate weight",
      body: "A Colorado HVAC company with 60 fresh reviews in the last 6 months will outrank one with 300 reviews from two years ago. We build a structured review-velocity system tied to job completion — not a one-time push.",
    },
  ],
  relatedLinks: [
    { to: "/hvac-marketing-colorado", label: "HVAC marketing in Colorado — full hub" },
    { to: "/colorado-springs-seo", label: "How our SEO process works" },
    { to: "/colorado-springs-google-maps", label: "Local SEO & Google Maps overview" },
    { to: "/hvac-web-design-colorado", label: "HVAC web design in Colorado" },
    { to: "/hvac-google-maps-colorado", label: "HVAC Google Maps optimization in Colorado" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/denver-digital-marketing", label: "Denver digital marketing" },
    { to: "/case-studies", label: "Colorado HVAC case studies" },
    { to: "/seo", label: "Our SEO service (all industries)" },
    { to: "/local-seo", label: "Local SEO overview" },
  ],
  faqs: [
    { q: "How long does HVAC SEO take to work in Colorado?", a: "Most Colorado HVAC clients see meaningful Map Pack movement in 60–120 days and stronger organic traffic within 4–6 months. Markets like Colorado Springs and Denver are competitive, so consistent monthly work is what compounds — not one-off projects." },
    { q: "Should HVAC companies invest in SEO or PPC first?", a: "If you need leads this week, start Google Ads. If you want a long-term lead engine that doesn't shut off when ad spend stops, start SEO. Most successful Colorado HVAC companies run both — ads for immediate flow, SEO for the durable foundation." },
    { q: "Will SEO help me get year-round leads, not just peak season?", a: "Yes — that's the whole point. We build content for shoulder-season terms (tune-ups, indoor air quality, maintenance plans) so your phone keeps ringing in spring and fall, not just June and January." },
    { q: "Do you only do HVAC SEO in Colorado Springs?", a: "No — we serve HVAC companies across all of Colorado: Denver, Fort Collins, Boulder, Pueblo, Grand Junction, Aurora, Greeley, Castle Rock, and the Front Range corridor. We're Colorado-only by choice." },
  ],
  serviceName: "HVAC SEO in Colorado",
  serviceDescription:
    "Local SEO and Map Pack optimization for HVAC companies across Colorado. Seasonal content, review velocity, and neighborhood-level targeting.",
  areaServed: "Colorado",
  // SEO: consolidate ranking signal onto the /hvac-marketing-colorado flagship hub.
  // This spoke targets "HVAC SEO" intent but content overlaps the marketing hub.
  canonicalOverride: "https://coloradowebimpressions.com/hvac-marketing-colorado",
};

export const Route = createFileRoute("/hvac-seo-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
